// Líneas de una venta cerrada en el vocabulario de las pantallas de ventas (Mis ventas y Ventas del
// backoffice). Puro: cada pantalla las pinta con sus propias filas.
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { itemDisplayName } from './itemDisplayName';
import { PRODUCT_CATEGORY_LABEL, SERVICE_CATEGORY_LABEL } from './productCategory';

export interface SaleLineIdentifiers {
    sku: string;
    values: string[];
}

export interface SaleLineRow {
    key: string;
    name: string;
    /** Categoría en español; `null` si la línea no trae ficha. */
    categoryLabel: string | null;
    /** SKU de la línea, cuántas piezas si son varias y el IMEI o serie si es un producto suelto. */
    detail: string;
    /** `null` si la línea no trae forma de pago. */
    paymentLabel: string | null;
    totalCents: number;
    /** Componentes de un kit que registraron IMEI o serie; el producto suelto lo lleva en `detail`. */
    identifiers: SaleLineIdentifiers[];
}

/** Categoría en español; un servicio se rotula «Servicio», no por su categoría de catálogo. */
function categoryLabelOf(product: SaleLine['product']): string | null {
    if (!product) return null;
    return product.itemType === 'SERVICE'
        ? SERVICE_CATEGORY_LABEL
        : PRODUCT_CATEGORY_LABEL[product.category];
}

const PAYMENT_LABEL: Record<NonNullable<SaleLine['paymentType']>, string> = {
    CREDIT: 'Crédito SureKeep',
    CASH: 'Contado',
};

/** Un regalo no se cobra: su forma de pago es «Regalo». */
export const GIFT_LABEL = 'Regalo';

function paymentLabelOf(line: SaleLine): string | null {
    if (line.role === 'GIFT') return GIFT_LABEL;
    return line.paymentType ? PAYMENT_LABEL[line.paymentType] : null;
}

/** El producto suelto trae un solo componente, él mismo: su IMEI va junto al SKU de la línea. */
function isSelfComponent(line: SaleLine): boolean {
    return line.components.length === 1 && line.components[0].sku === line.sku;
}

function toRow(line: SaleLine): SaleLineRow {
    const name = itemDisplayName({ sku: line.sku, ...line.product });
    const base = line.qty > 1 ? `${line.sku} × ${String(line.qty)}` : line.sku;
    const identifiers = line.components
        .filter((c) => c.identifiers.length > 0)
        .map((c) => ({ sku: c.sku, values: c.identifiers }));
    const self = isSelfComponent(line);
    const ownValues = self ? (identifiers[0]?.values ?? []) : [];
    return {
        key: line.lineId,
        name,
        categoryLabel: categoryLabelOf(line.product),
        detail: ownValues.length > 0 ? `${base} · ${ownValues.join(', ')}` : base,
        paymentLabel: paymentLabelOf(line),
        totalCents: line.basePriceCents * line.qty,
        identifiers: self ? [] : identifiers,
    };
}

/** `null` en las ventas anteriores a las líneas: esas se siguen pintando por `sku`/`imei`. */
export function saleLineRows(lines: SaleLine[] | undefined): SaleLineRow[] | null {
    if (!lines || lines.length === 0) return null;
    return lines.map(toRow);
}
